import {
  mergeSpecs,
  signTemplates,
  sanitizeSpecs,
  sanitizeCopy,
  themes,
} from '@two90signs/290-renderer';

const reducer = (state, action) => {
  // do not sanitize copy when "copy" is the thing being changed
  // this is so that users can override the auto-generated copy with their own
  let shouldSanitizeCopy = true;

  const {
    templateID,
    specs: signSpecs,
    copy,
    quantity,
  } = state || {};

  let reducedSign = { ...state };

  const signTemplate = signTemplates.find((template) => template.templateID === templateID);

  const {
    specs: templateSpecs,
    alternateSpecs: alternateTemplateSpecs,
  } = signTemplate || {};

  const {
    type,
    payload,
    project,
    sanitizerOptions,
  } = action;

  const {
    theme,
    specs: themeSpecs,
  } = project || {};

  const baseThemeSpecs = themes[theme];
  const mergedTheme = mergeSpecs(baseThemeSpecs, themeSpecs);

  switch (type) {
    case 'RESET_SIGN': {
      const templateSpecsToUse = alternateTemplateSpecs?.[mergedTheme.signSystem] || templateSpecs;
      const { inserts: insertsFromTemplate } = templateSpecsToUse;

      // extract copy from inserts for first sign only (quantity resets to 1)
      const resetCopy = [{
        inserts: insertsFromTemplate.map(({ lines }) => ({ lines })),
      }];

      reducedSign = {
        ...reducedSign,
        copy: resetCopy,
        wallColor: undefined,
        quantity: 1,
        specs: templateSpecsToUse,
      };

      break;
    }

    case 'UPDATE_SIGN': {
      reducedSign = {
        ...reducedSign,
        ...payload,
      };

      break;
    }

    case 'UPDATE_LINE': {
      const {
        signIndex,
        insertIndex,
        lineIndex,
        specs: incomingLineSpecs,
      } = payload;

      // only sanitize when the line being updated DOES NOT contain copy updates
      shouldSanitizeCopy = incomingLineSpecs.copy === undefined;

      const withUpdatedCopy = [...copy];

      if (!withUpdatedCopy[signIndex]) withUpdatedCopy[signIndex] = {};
      if (!withUpdatedCopy[signIndex].inserts) withUpdatedCopy[signIndex].inserts = [];
      if (!withUpdatedCopy[signIndex].inserts[insertIndex]) withUpdatedCopy[signIndex].inserts[insertIndex] = {};
      if (!withUpdatedCopy[signIndex].inserts[insertIndex]?.lines) withUpdatedCopy[signIndex].inserts[insertIndex].lines = [];
      if (!withUpdatedCopy[signIndex].inserts[insertIndex]?.lines[lineIndex]) withUpdatedCopy[signIndex].inserts[insertIndex].lines[lineIndex] = {};

      withUpdatedCopy[signIndex].inserts[insertIndex].lines[lineIndex] = {
        ...withUpdatedCopy[signIndex].inserts[insertIndex].lines[lineIndex],
        ...incomingLineSpecs,
      };

      reducedSign = {
        ...reducedSign,
        copy: withUpdatedCopy,
      };

      break;
    }

    case 'UPDATE_SPEC': {
      const { specKey, specValue } = payload;

      // alternate specs are sign system dependent, so need to re-merge the template if the sign system changes
      const shouldMergeTemplate = specKey === 'signSystem';
      let templateSpecsToUse = templateSpecs;
      let resetCopy = [...copy];
      let resetQuantity = quantity;

      if (shouldMergeTemplate) {
        const mergedSignSystem = specValue || mergedTheme?.signSystem;
        // if there are alternative specs, then the copy has to be reset because it may be incompatible
        if (alternateTemplateSpecs) {
          templateSpecsToUse = alternateTemplateSpecs?.[mergedSignSystem] || templateSpecs;
          const { inserts: insertsFromTemplate } = templateSpecsToUse;
          resetCopy = [{
            inserts: insertsFromTemplate.map(({ lines }) => ({ lines })),
          }];
          resetQuantity = 1;
        }
      }

      reducedSign = {
        ...reducedSign,
        specs: {
          ...shouldMergeTemplate ? mergeSpecs(signSpecs, templateSpecsToUse) : signSpecs,
          [specKey]: specValue,
        },
        copy: resetCopy,
        quantity: resetQuantity,
      };

      break;
    }

    case 'UPDATE_INSERT': {
      const { index, specKey, specValue } = payload;

      const updatedInserts = signSpecs?.inserts || [];

      if (updatedInserts[index]) {
        updatedInserts[index] = {
          ...updatedInserts[index],
          [specKey]: specValue,
        };
      }

      const withUpdatedInserts = {
        ...signSpecs,
        inserts: updatedInserts,
      };

      reducedSign = {
        ...reducedSign,
        specs: withUpdatedInserts,
      };

      break;
    }

    case 'UPDATE_BACKER': {
      const { index, specs } = payload;

      if (specs && typeof specs === 'object') {
        const updatedBackers = [...signSpecs?.backers || []];

        if (updatedBackers[index]) {
          updatedBackers[index] = {
            ...updatedBackers[index],
            ...specs,
          };
        }

        reducedSign = {
          ...reducedSign,
          specs: {
            ...signSpecs,
            backers: updatedBackers,
          },
        };
      }

      break;
    }

    default: {
      break;
    }
  }

  const {
    specs: reducedSpecs,
    copy: reducedCopy,
  } = reducedSign;

  const sanitizedCopy = shouldSanitizeCopy ? sanitizeCopy(reducedCopy, templateID) : reducedCopy;

  const sanitizedSignSpecs = sanitizeSpecs({
    baseSpecs: mergedTheme,
    specs: reducedSpecs,
    selectedSignTemplate: templateID,
    theme,
    options: sanitizerOptions,
    copy: sanitizedCopy,
    template: templateID,
  });

  const sanitizedSign = {
    ...reducedSign,
    specs: sanitizedSignSpecs,
    copy: sanitizedCopy,
  };

  return sanitizedSign;
};

export default reducer;
